import { defineStore } from 'pinia'
import type { AtlasDataset as _AtlasDatasetWindow } from '../types/atlas-intel.types'

declare global {
  interface Window {
    __atlasDataset?: _AtlasDatasetWindow | null
  }
}
import { ref, computed, watch } from 'vue'
import {
  loadAtlasDataset,
  loadMetricsFromNestApi,
  FILE_KEY_BY_NAME,
  readFileAsText
} from '../services/atlas-intel/atlas-data-loader'
import { authHeaders } from '../query-ai/lib/auth'

/** Resolve the platform-data API base URL.
 *  - localhost → use Webpack dev-server proxy (relative URL)
 *  - staging/prod → absolute URL so requests don't hit devtools.msgsndr.net (static host only)
 */
function apiBase(): string {
  if (typeof window === 'undefined') return ''
  const h = window.location.hostname
  if (h === 'localhost' || h === '127.0.0.1') return ''
  return process.env.VUE_APP_AI_API_URL || 'https://dev.services.leadconnectorhq.com'
}
function platformDataUrl(path: string): string {
  return `${apiBase()}${path}`
}

function friendlyAtlasLoadError(e: unknown): string {
  const msg = e instanceof Error ? e.message : String(e)
  if (/signal timed out|timeout|timed out/i.test(msg)) {
    return 'Atlas data is still loading from ClickHouse. The request timed out; refresh to retry.'
  }
  return msg || 'Failed to load data'
}
import type {
  AtlasDataset,
  AtlasProject,
  SourceProfile,
  AtlasFileKey,
  SourceType,
  TimeRange,
  TierRecord,
  CollectConfig,
  JobReport
} from '../types/atlas-intel.types'
import { ATLAS_FILE_NAMES } from '../types/atlas-intel.types'

const STORAGE_KEY = 'atlas_intel_profiles'
const ACTIVE_KEY = 'atlas_intel_active_profile'
const OPENAI_KEY = 'atlas_intel_openai_key'
const TIMERANGE_KEY = 'atlas_intel_timerange'
const REFRESH_KEY = 'atlas_intel_refresh'
const COLLECT_CONFIG_KEY = 'atlas_intel_collect_config'
const FILTER_CLUSTER_KEY = 'atlas_intel_filter_cluster'
const FILTER_PROJECT_KEY = 'atlas_intel_filter_project'
const COLLECTION_JOB_KEY = 'atlas_intel_collection_job'
const LAST_REPORT_JOB_KEY = 'atlas_intel_last_report_job'
const COST_PRESET_KEY = 'atlas_intel_cost_preset'
const COST_CUSTOM_START_KEY = 'atlas_intel_cost_custom_start'
const COST_CUSTOM_END_KEY = 'atlas_intel_cost_custom_end'
const COST_CLUSTER_FILTER_KEY = 'atlas_intel_cost_cluster_filter'
const DATE_RANGE_BY_PAGE_KEY = 'atlas_intel_date_range_by_page'

export type CostPreset = 'current' | 'ytd' | 'last3m' | 'last6m' | 'last12m' | 'all'

interface CollectionReportState {
  report: JobReport
  progress?: string[]
  mode?: string
  updatedAt?: string
}

interface StopCollectionResult {
  message?: string
  stoppedJobs?: Array<{ jobId: string; status: 'cancelled' }>
  alreadyTerminal?: Array<{ jobId: string; status: string }>
  killed?: { mongosh: number; atlas: number }
  lockRelease?: {
    released: boolean
    priorHolder?: string
    priorExpiresAt?: string
    message?: string
  }
}

/**
 * Per-page user-selected date range, keyed by `route.path`.
 *
 * Why per-page (and not a single global current range)?
 *   - The Atlas Intel pages mix point-in-time, fixed-window, and
 *     range-aware data shapes (see services/atlas-intel/page-scope.ts).
 *     A single global range would either silently apply to surfaces it
 *     can't filter (storage / indexes), or get reset when the user
 *     navigates away from a range-aware page.
 *   - Atlas billing buckets are monthly while metrics buckets are
 *     hourly; the user's "natural" window is different on each. Storing
 *     them separately means the user doesn't have to re-pick when
 *     hopping between costs and recommendations.
 *   - URL state remains the source of truth for sharing / reload — the
 *     store is just a same-session memory so a user who navigates away
 *     and back without a deep-link sees their previous picks restored.
 */
export interface PageDateRange {
  from: string
  to: string
}

/** First day of a month offset from `from` by `offsetMonths`, as YYYY-MM-01. */
function fmtMonthOffset(from: Date, offsetMonths: number): string {
  const d = new Date(from.getFullYear(), from.getMonth() + offsetMonths, 1)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`
}

const STALE_THRESHOLD_MS = 60_000

function loadProfilesFromStorage(): SourceProfile[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    return raw ? JSON.parse(raw) : []
  } catch {
    return []
  }
}

function saveProfilesToStorage(profiles: SourceProfile[]) {
  // Strip loadedFiles (file content) — too large for localStorage (5 MB quota).
  // localFiles profiles survive in memory for the session; on reload the user re-uploads.
  const toSave = profiles.map((p) => {
    if (p.type === 'localFiles') {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { loadedFiles: _f, ...rest } = p
      return rest
    }
    return p
  })
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(toSave))
  } catch {
    // Silently swallow any remaining quota errors (e.g. many GCP profiles)
  }
}

// ── Probe types (shared between store internals and Diagnostics UI) ───────────
export type ProbeStatus = 'ok' | 'timeout' | 'network_blocked' | 'auth_error' | 'error'
export type EndpointType = 'private' | 'privateSrv' | 'standardSrv'

export interface EndpointProbeResult {
  type: EndpointType
  uri: string
  status: ProbeStatus
  ms: number
  error?: string
}

export interface ClusterProbeReport {
  cluster: string
  projectId: string
  endpoints: EndpointProbeResult[]
  recommended: EndpointType | null
}

export interface ConnectivityProbeReport {
  probed_at: string
  environment: { ATLAS_USE_PRIVATE_SRV: string; orgId: string; credentialsConfigured: boolean }
  summary: { total: number; reachable: number; blocked: number }
  clusters: ClusterProbeReport[]
}

export const useAtlasIntelStore = defineStore('atlasIntel', () => {
  // ─── State ────────────────────────────────────────────────────────────────
  const profiles = ref<SourceProfile[]>(loadProfilesFromStorage())
  const activeProfileId = ref<string | null>(localStorage.getItem(ACTIVE_KEY))
  const dataset = ref<AtlasDataset | null>(null)
  const loading = ref(false)
  const loadError = ref<string | null>(null)
  const metricsLoading = ref(false)
  const metricsError = ref<string | null>(null)
  const liveRefreshEnabled = ref(false)
  const liveRefreshInterval = ref<ReturnType<typeof setInterval> | null>(null)
  const searchQuery = ref('')
  const openaiKey = ref<string>(localStorage.getItem(OPENAI_KEY) ?? '')

  // Time range and refresh interval (persisted)
  // Default '3m' = 2 complete months + current MTD — keeps cost KPIs meaningful out of the box
  const _storedTimeRange = localStorage.getItem(TIMERANGE_KEY) as TimeRange | null
  const timeRange = ref<TimeRange>(
    // Migrate legacy day-based values to month equivalents
    _storedTimeRange === '7d' || _storedTimeRange === '14d' || _storedTimeRange === '30d'
      ? '3m'
      : _storedTimeRange === '90d'
        ? '3m'
        : (_storedTimeRange as TimeRange) || '3m'
  )
  const refreshInterval = ref<number>(
    parseInt(localStorage.getItem(REFRESH_KEY) || '30000') || 30000
  )

  // Global filters — multi-select arrays (persisted across page navigations)
  const filterProjectIds = ref<string[]>(
    (() => {
      try {
        return JSON.parse(localStorage.getItem(FILTER_PROJECT_KEY) ?? '[]')
      } catch {
        return []
      }
    })()
  )
  const filterClusters = ref<string[]>(
    (() => {
      try {
        return JSON.parse(localStorage.getItem(FILTER_CLUSTER_KEY) ?? '[]')
      } catch {
        return []
      }
    })()
  )

  // Backward-compat single-value accessors (first item or empty string)
  const filterProjectId = computed(() => filterProjectIds.value[0] ?? '')
  const filterCluster = computed(() => filterClusters.value[0] ?? '')

  function _saveFilterProjects() {
    if (filterProjectIds.value.length)
      localStorage.setItem(FILTER_PROJECT_KEY, JSON.stringify(filterProjectIds.value))
    else localStorage.removeItem(FILTER_PROJECT_KEY)
  }
  function _saveFilterClusters() {
    if (filterClusters.value.length)
      localStorage.setItem(FILTER_CLUSTER_KEY, JSON.stringify(filterClusters.value))
    else localStorage.removeItem(FILTER_CLUSTER_KEY)
  }

  /** Toggle a single project in/out of the active filter. */
  function toggleFilterProject(pid: string) {
    const idx = filterProjectIds.value.indexOf(pid)
    if (idx === -1) filterProjectIds.value = [...filterProjectIds.value, pid]
    else filterProjectIds.value = filterProjectIds.value.filter((p) => p !== pid)
    // Clear clusters that no longer belong to any selected project
    filterClusters.value = []
    _saveFilterProjects()
    _saveFilterClusters()
  }

  /** Replace the entire project selection. Pass [] to clear. */
  function setFilterProjectIds(pids: string[]) {
    filterProjectIds.value = pids
    filterClusters.value = []
    _saveFilterProjects()
    _saveFilterClusters()
  }

  /** Toggle a single cluster in/out of the active filter. */
  function toggleFilterCluster(cluster: string) {
    const idx = filterClusters.value.indexOf(cluster)
    if (idx === -1) filterClusters.value = [...filterClusters.value, cluster]
    else filterClusters.value = filterClusters.value.filter((c) => c !== cluster)
    _saveFilterClusters()
  }

  /** Replace the entire cluster selection. Pass [] to clear. */
  function setFilterClusters(clusters: string[]) {
    filterClusters.value = clusters
    _saveFilterClusters()
  }

  // Backward-compat single-value setters
  function setFilterCluster(cluster: string) {
    filterClusters.value = cluster ? [cluster] : []
    _saveFilterClusters()
  }
  function setFilterProjectId(pid: string) {
    filterProjectIds.value = pid ? [pid] : []
    filterClusters.value = []
    _saveFilterProjects()
    _saveFilterClusters()
  }

  // Smart cache tracking
  const lastFetchedAt = ref<number>(0)
  const lastTimeRange = ref<string>('')

  // Data freshness / recollection state
  const lastCollectedAt = ref<string | null>(null)
  const collectionJobId = ref<string | null>(localStorage.getItem(COLLECTION_JOB_KEY))
  const collectJobLogs = ref<string[]>([])
  // Structured failure report from /collect/status (the active job's report
  // field) or /collect/report/:jobId. Mirrored into a dedicated ref so the
  // admin Failure Report card can subscribe without re-deriving from the
  // jobs array on every poll.
  const currentJobReport = ref<JobReport | null>(null)
  // Restore in-progress state if a job was running before the page was refreshed
  const isCollecting = ref(collectionJobId.value !== null)
  const collectConfig = ref<CollectConfig | null>(
    (() => {
      try {
        return JSON.parse(localStorage.getItem(COLLECT_CONFIG_KEY) ?? 'null')
      } catch {
        return null
      }
    })()
  )

  // Projects available for collection (loaded from /collect/projects)
  const availableProjects = ref<AtlasProject[]>([])
  const credentialsConfigured = ref(false)
  const projectsLoaded = ref(false)

  // ─── Getters ──────────────────────────────────────────────────────────────
  const activeProfile = computed(
    () => profiles.value.find((p) => p.id === activeProfileId.value) ?? null
  )

  const hasData = computed(() => {
    if (dataset.value === null) return false
    const d = dataset.value
    return (
      d.costs.length > 0 ||
      d.storage.length > 0 ||
      d.ops.length > 0 ||
      d.indexes.length > 0 ||
      d.server.length > 0 ||
      d.delta != null ||
      (d.deepReport?.recommendations?.length ?? 0) > 0 ||
      (d.tiers?.length ?? 0) > 0
    )
  })

  const fileStatuses = computed(() => {
    if (!dataset.value) return {}
    return dataset.value.fileStatus
  })

  // ─── Available project IDs derived from loaded data ──────────────────────
  /**
   * Distinct project IDs in the current dataset, each paired with a display
   * name resolved from the collect-projects list when available.
   */
  const availableDataProjects = computed(() => {
    const ids = new Set<string>()
    for (const t of dataset.value?.tiers ?? []) if (t.project_id) ids.add(t.project_id)
    for (const r of dataset.value?.costs ?? []) if (r.project_id) ids.add(r.project_id)
    return [...ids].sort().map((pid) => ({
      id: pid,
      name: availableProjects.value.find((p) => p.id === pid)?.name ?? pid
    }))
  })

  // ─── Available cluster names (scoped to selected projects when set) ─────────
  // Derived from tiers first; falls back to storage/ops/costs cluster names when tiers is empty
  const availableClusters = computed(() => {
    const pids = filterProjectIds.value
    const tiers = dataset.value?.tiers ?? []
    const scopedTiers = pids.length ? tiers.filter((t) => pids.includes(t.project_id ?? '')) : tiers
    if (scopedTiers.length > 0) {
      return [...new Set(scopedTiers.map((t) => t.name))].sort()
    }
    // Fallback: derive from storage / ops / costs
    const names = new Set<string>()
    for (const r of dataset.value?.storage ?? []) {
      if (!pids.length || pids.includes(r.project_id ?? '')) names.add(r.cluster)
    }
    for (const r of dataset.value?.ops ?? []) {
      if (!pids.length || pids.includes(r.project_id ?? '')) names.add(r.cluster)
    }
    for (const r of dataset.value?.costs ?? []) {
      if (!pids.length || pids.includes(r.project_id ?? '')) names.add(r.c)
    }
    return [...names].sort()
  })

  // ─── Filtered data (respects filterCluster + time range for costs) ────────

  /**
   * First day of the current calendar month in YYYY-MM-01 format.
   * The server sends this too (dataset.currentMonthKey), but we compute it
   * locally as a reactive fallback so cost cards work offline / from file sources.
   */
  const currentMonthKey = computed(() => {
    const serverKey = dataset.value?.currentMonthKey
    if (serverKey) return serverKey
    const now = new Date()
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`
  })

  // ── Service filter (cost-page only) ────────────────────────────────────────
  const filterServices = ref<string[]>([])

  function toggleFilterService(svc: string) {
    const idx = filterServices.value.indexOf(svc)
    if (idx === -1) filterServices.value = [...filterServices.value, svc]
    else filterServices.value = filterServices.value.filter((s) => s !== svc)
  }

  function setFilterServices(svcs: string[]) {
    filterServices.value = svcs
  }

  /**
   * Services derived from the costsByService dataset (which uses groupBy:'services').
   * Do NOT use costs.s — that field is "Unknown" for cluster-level records.
   */
  const availableServices = computed(() => {
    const svcs = new Set<string>()
    for (const r of dataset.value?.costsByService ?? []) if (r.s) svcs.add(r.s)
    return [...svcs].filter(Boolean).sort()
  })

  /** costsByService rows filtered by project and the cost-page time range. */
  const filteredCostsByService = computed(() => {
    let rows = dataset.value?.costsByService ?? []
    if (filterProjectIds.value.length)
      rows = rows.filter((r) => filterProjectIds.value.includes(r.project_id ?? ''))
    const { start, end } = costMonthBounds.value
    if (start) rows = rows.filter((r) => r.d >= start)
    if (end) rows = rows.filter((r) => r.d <= end)
    return rows
  })

  // ── Snapshot picker ─────────────────────────────────────────────────────────
  const selectedSnapshotId = ref<string | null>(null)
  const snapshotList = ref<Array<{ snapshotId: string; collectedAt: string; tables: string[] }>>([])

  function setSelectedSnapshot(snapshotId: string | null) {
    selectedSnapshotId.value = snapshotId
  }

  async function loadSnapshotList() {
    try {
      const resp = await fetch(
        platformDataUrl('/platform-data/query-ai/atlas-intel/snapshots'),
        { headers: authHeaders(), signal: AbortSignal.timeout(10000) }
      )
      if (!resp.ok) return
      const data = await resp.json()
      snapshotList.value = Array.isArray(data) ? data : (data.snapshots ?? [])
    } catch {
      /* ignore — snapshots endpoint may not exist yet */
    }
  }

  // ── Cost-page time range (independent of global timeRange) ──────────────────
  const costPreset = ref<CostPreset>(
    (localStorage.getItem(COST_PRESET_KEY) as CostPreset) ?? 'last3m'
  )
  const costCustomStart = ref<string>(localStorage.getItem(COST_CUSTOM_START_KEY) ?? '')
  const costCustomEnd = ref<string>(localStorage.getItem(COST_CUSTOM_END_KEY) ?? '')

  // ── Cost-page cluster filter (separate from global filterClusters) ───────────
  const costFilterClusters = ref<string[]>(
    (() => {
      try {
        return JSON.parse(localStorage.getItem(COST_CLUSTER_FILTER_KEY) ?? '[]')
      } catch {
        return []
      }
    })()
  )

  // ── Per-page user-selected date ranges (P2: scope enforcement) ──────────────
  // Keyed by `route.path`. Persisted across page reloads so a user who navigates
  // back to /costs without a `?from&to` deep-link sees their last selection
  // restored. URL params, when present, still take precedence in DateRangeBar.
  const dateRangeByPage = ref<Record<string, PageDateRange>>(
    (() => {
      try {
        return JSON.parse(localStorage.getItem(DATE_RANGE_BY_PAGE_KEY) ?? '{}')
      } catch {
        return {}
      }
    })()
  )

  function _saveDateRangeByPage() {
    try {
      const trimmed: Record<string, PageDateRange> = {}
      for (const [k, v] of Object.entries(dateRangeByPage.value)) {
        if (v?.from && v?.to) trimmed[k] = v
      }
      localStorage.setItem(DATE_RANGE_BY_PAGE_KEY, JSON.stringify(trimmed))
    } catch {
      /* quota — silently drop; URL state still wins on reload */
    }
  }

  /**
   * Persist the user's date range for a specific route path. No spill across
   * routes: each page reads only its own entry. Pass `from <= to` (caller
   * normalizes — the date picker swaps if the user picks them in reverse).
   */
  function setDateRangeForPage(routePath: string, from: string, to: string) {
    if (!routePath || !from || !to) return
    dateRangeByPage.value = {
      ...dateRangeByPage.value,
      [routePath]: { from, to },
    }
    _saveDateRangeByPage()
  }

  function clearDateRangeForPage(routePath: string) {
    if (!routePath) return
    if (!(routePath in dateRangeByPage.value)) return
    const next = { ...dateRangeByPage.value }
    delete next[routePath]
    dateRangeByPage.value = next
    _saveDateRangeByPage()
  }

  /**
   * The effective [start, end] month keys for the cost page based on the selected preset
   * or custom range. Both are YYYY-MM-01. `end` null means "up to latest available".
   */
  const costMonthBounds = computed((): { start: string | null; end: string | null } => {
    if (costCustomStart.value && costCustomEnd.value) {
      return { start: costCustomStart.value, end: costCustomEnd.value }
    }
    const now = new Date()
    switch (costPreset.value) {
      case 'current': {
        const key = fmtMonthOffset(now, 0)
        return { start: key, end: key }
      }
      case 'ytd':
        return { start: `${now.getFullYear()}-01-01`, end: null }
      case 'last3m':
        return { start: fmtMonthOffset(now, -2), end: null }
      case 'last6m':
        return { start: fmtMonthOffset(now, -5), end: null }
      case 'last12m':
        return { start: fmtMonthOffset(now, -11), end: null }
      case 'all':
      default:
        return { start: null, end: null }
    }
  })

  /** All unique cluster names present in cost records (respects project filter only). */
  const availableCostClusters = computed(() => {
    const names = new Set<string>()
    let rows = dataset.value?.costs ?? []
    if (filterProjectIds.value.length)
      rows = rows.filter((r) => filterProjectIds.value.includes(r.project_id ?? ''))
    for (const r of rows) if (r.c) names.add(r.c)
    return [...names].sort()
  })

  function setCostPreset(preset: CostPreset) {
    costPreset.value = preset
    costCustomStart.value = ''
    costCustomEnd.value = ''
    localStorage.setItem(COST_PRESET_KEY, preset)
    localStorage.removeItem(COST_CUSTOM_START_KEY)
    localStorage.removeItem(COST_CUSTOM_END_KEY)
  }

  function setCostCustomRange(start: string, end: string) {
    costCustomStart.value = start
    costCustomEnd.value = end
    localStorage.setItem(COST_CUSTOM_START_KEY, start)
    localStorage.setItem(COST_CUSTOM_END_KEY, end)
  }

  function toggleCostFilterCluster(cluster: string) {
    const idx = costFilterClusters.value.indexOf(cluster)
    if (idx === -1) costFilterClusters.value = [...costFilterClusters.value, cluster]
    else costFilterClusters.value = costFilterClusters.value.filter((c) => c !== cluster)
    localStorage.setItem(COST_CLUSTER_FILTER_KEY, JSON.stringify(costFilterClusters.value))
  }

  function setCostFilterClusters(clusters: string[]) {
    costFilterClusters.value = clusters
    if (clusters.length) localStorage.setItem(COST_CLUSTER_FILTER_KEY, JSON.stringify(clusters))
    else localStorage.removeItem(COST_CLUSTER_FILTER_KEY)
  }

  // Phase 4 / D8 (2026-05-03): cluster-name predicates accept either the
  // bare cluster name (legacy UI selection) or a composite key of the
  // form `${project_id}::${cluster}`. Two projects can legally share a
  // cluster display name (same tenant, different staging/prod buckets),
  // so when the user explicitly opts into a project+cluster pair (or
  // the dropdown emits composite keys in a future revision) the
  // filter no longer collapses both projects' rows. Existing UI
  // (which still emits bare cluster names) keeps working unchanged.
  function _matchesClusterFilter(row: { cluster?: string; c?: string; project_id?: string | null }): boolean {
    if (!filterClusters.value.length) return true
    const clusterName = row.cluster ?? row.c ?? ''
    if (filterClusters.value.includes(clusterName)) return true
    const projectId = row.project_id ?? ''
    if (projectId && filterClusters.value.includes(`${projectId}::${clusterName}`)) return true
    return false
  }

  /**
   * All cost rows filtered by project, cluster, service, and the cost-page time range.
   * The current (partial) calendar month is INCLUDED — use isCurrentMonthPartial
   * to display a "Partial" badge rather than hiding the data.
   */
  const filteredCosts = computed(() => {
    let rows = dataset.value?.costs ?? []
    if (filterProjectIds.value.length)
      rows = rows.filter((r) => filterProjectIds.value.includes(r.project_id ?? ''))
    if (filterClusters.value.length) rows = rows.filter((r) => _matchesClusterFilter(r))
    if (costFilterClusters.value.length)
      rows = rows.filter((r) => costFilterClusters.value.includes(r.c))
    if (filterServices.value.length)
      rows = rows.filter((r) => filterServices.value.includes(r.s ?? ''))
    const { start, end } = costMonthBounds.value
    if (start) rows = rows.filter((r) => r.d >= start)
    if (end) rows = rows.filter((r) => r.d <= end)
    return rows
  })

  const filteredStorage = computed(() => {
    let rows = dataset.value?.storage ?? []
    if (filterProjectIds.value.length)
      rows = rows.filter((r) => filterProjectIds.value.includes(r.project_id ?? ''))
    if (filterClusters.value.length)
      rows = rows.filter((r) => _matchesClusterFilter(r))
    return rows
  })

  const filteredOps = computed(() => {
    let rows = dataset.value?.ops ?? []
    if (filterProjectIds.value.length)
      rows = rows.filter((r) => filterProjectIds.value.includes(r.project_id ?? ''))
    if (filterClusters.value.length)
      rows = rows.filter((r) => _matchesClusterFilter(r))
    return rows
  })

  const filteredIndexes = computed(() => {
    let rows = dataset.value?.indexes ?? []
    if (filterProjectIds.value.length)
      rows = rows.filter((r) => filterProjectIds.value.includes(r.project_id ?? ''))
    if (filterClusters.value.length)
      rows = rows.filter((r) => _matchesClusterFilter(r))
    return rows
  })

  // ─── Derived cost data (all use filteredCosts) ────────────────────────────
  const months = computed(() => [...new Set(filteredCosts.value.map((r) => r.d))].sort())

  const numMonths = computed(() => months.value.length || 1)

  /** True when the dataset contains cost data for the current calendar month (it is partial). */
  const isCurrentMonthPartial = computed(() =>
    filteredCosts.value.some((r) => r.d === currentMonthKey.value)
  )

  /** All months in filteredCosts that are BEFORE the current (partial) month. */
  const completeMonths = computed(() => months.value.filter((m) => m < currentMonthKey.value))

  /** Most recently COMPLETED month key (e.g. 2026-03-01 when April is in progress). */
  const latestCompleteMonth = computed(
    () => completeMonths.value[completeMonths.value.length - 1] ?? ''
  )

  const costByCluster = computed(() => {
    const m: Record<string, { total: number; byMonth: Record<string, number> }> = {}
    for (const r of filteredCosts.value) {
      if (!m[r.c]) m[r.c] = { total: 0, byMonth: {} }
      m[r.c].total += r.a
      m[r.c].byMonth[r.d] = (m[r.c].byMonth[r.d] ?? 0) + r.a
    }
    return m
  })

  const totalCost = computed(() =>
    Object.values(costByCluster.value).reduce((s, v) => s + v.total, 0)
  )

  /** Average monthly cost computed over COMPLETE months only (excludes partial current month). */
  const avgMonthlyCost = computed(() => {
    const n = completeMonths.value.length || 1
    const completeTotal = filteredCosts.value
      .filter((r) => r.d < currentMonthKey.value)
      .reduce((s, r) => s + r.a, 0)
    return completeTotal / n
  })

  const latestMonth = computed(() => months.value[months.value.length - 1] ?? '')
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _prevMonth = computed(() => months.value[months.value.length - 2] ?? '')

  /** Cost for the most recent COMPLETE calendar month. */
  const latestMonthCost = computed(() => {
    if (!latestCompleteMonth.value) return 0
    return filteredCosts.value
      .filter((r) => r.d === latestCompleteMonth.value)
      .reduce((s, r) => s + r.a, 0)
  })

  /** Cost for the CURRENT partial month (0 if no partial data). */
  const monthToDateCost = computed(() => {
    if (!isCurrentMonthPartial.value) return 0
    return filteredCosts.value
      .filter((r) => r.d === currentMonthKey.value)
      .reduce((s, r) => s + r.a, 0)
  })

  const prevMonthCost = computed(() => {
    const prev = completeMonths.value[completeMonths.value.length - 2] ?? ''
    if (!prev) return 0
    return filteredCosts.value.filter((r) => r.d === prev).reduce((s, r) => s + r.a, 0)
  })

  const momChange = computed(() => {
    if (!prevMonthCost.value) return 0
    return ((latestMonthCost.value - prevMonthCost.value) / prevMonthCost.value) * 100
  })

  /** Org-level cost rollup. Includes current partial month same as cluster costs. */
  const orgCosts = computed(() => dataset.value?.orgCosts ?? [])

  const tierMap = computed(() => {
    const m: Record<string, TierRecord> = {}
    for (const t of dataset.value?.tiers ?? []) m[t.name] = t
    return m
  })

  const clustersSortedByCost = computed(() =>
    Object.entries(costByCluster.value)
      .map(([name, d]) => ({
        name,
        avgMonthly: d.total / numMonths.value,
        tier: tierMap.value[name]
      }))
      .sort((a, b) => b.avgMonthly - a.avgMonthly)
  )

  // ─── Derived storage + ops enrichment (uses filtered data) ──────────────
  const enrichedStorage = computed(() => {
    if (!dataset.value) return []
    const opsMap: Record<string, (typeof dataset.value.ops)[0]> = {}
    for (const o of filteredOps.value) opsMap[`${o.cluster}::${o.database}::${o.collection}`] = o

    const clusterDisk: Record<string, number> = {}
    for (const s of filteredStorage.value) {
      clusterDisk[s.cluster] =
        (clusterDisk[s.cluster] ?? 0) + (parseFloat(s.total_disk_mb || '0') || 0)
    }

    return filteredStorage.value.map((s) => {
      const cc = costByCluster.value[s.cluster]
      const avgMonthly = cc ? cc.total / numMonths.value : 0
      const diskMb = parseFloat(s.total_disk_mb || '0') || 0
      const clusterTotal = clusterDisk[s.cluster] || 1
      const estCost = avgMonthly * (diskMb / clusterTotal)
      const o = opsMap[`${s.cluster}::${s.database}::${s.collection}`]
      const reads = parseInt(o?.reads || '0')
      const writes = parseInt(o?.writes || '0')
      return { ...s, est_cost: estCost, reads, writes, total_ops: reads + writes }
    })
  })

  // ─── Actions ──────────────────────────────────────────────────────────────
  function addProfile(profile: Omit<SourceProfile, 'id' | 'createdAt'>): SourceProfile {
    const newProfile: SourceProfile = {
      ...profile,
      id: `profile_${Date.now()}`,
      createdAt: new Date().toISOString()
    }
    profiles.value.push(newProfile)
    saveProfilesToStorage(profiles.value)
    return newProfile
  }

  function updateProfile(id: string, updates: Partial<SourceProfile>) {
    const idx = profiles.value.findIndex((p) => p.id === id)
    if (idx >= 0) {
      profiles.value[idx] = { ...profiles.value[idx], ...updates }
      saveProfilesToStorage(profiles.value)
    }
  }

  function deleteProfile(id: string) {
    profiles.value = profiles.value.filter((p) => p.id !== id)
    saveProfilesToStorage(profiles.value)
    if (activeProfileId.value === id) {
      setActiveProfile(null)
      dataset.value = null
      if (typeof window !== 'undefined') window.__atlasDataset = null
    }
  }

  function setActiveProfile(id: string | null) {
    activeProfileId.value = id
    if (id) {
      localStorage.setItem(ACTIVE_KEY, id)
    } else {
      localStorage.removeItem(ACTIVE_KEY)
    }
  }

  async function loadData(profileId?: string, opts?: { force?: boolean }) {
    const pid = profileId ?? activeProfileId.value
    const profile = profiles.value.find((p) => p.id === pid)
    if (!profile) return

    // Smart cache: skip fetch for nestApi if data is fresh and not forced
    if (
      !opts?.force &&
      dataset.value &&
      profile.id === activeProfileId.value &&
      profile.type === 'nestApi' &&
      lastTimeRange.value === timeRange.value &&
      Date.now() - lastFetchedAt.value < STALE_THRESHOLD_MS
    ) {
      return
    }

    loading.value = true
    loadError.value = null
    metricsError.value = null
    try {
      setActiveProfile(profile.id)
      const range = profile.type === 'nestApi' ? timeRange.value : undefined
      const snapshotId = profile.type === 'nestApi' ? (selectedSnapshotId.value ?? undefined) : undefined
      dataset.value = await loadAtlasDataset(profile, range, snapshotId)
      lastFetchedAt.value = Date.now()
      lastTimeRange.value = timeRange.value
      if (dataset.value?.lastCollectedAt) lastCollectedAt.value = dataset.value.lastCollectedAt
      if (typeof window !== 'undefined') window.__atlasDataset = dataset.value
    } catch (e) {
      loadError.value = friendlyAtlasLoadError(e)
      // Clear stale data so charts from the previous profile aren't shown alongside the error
      dataset.value = null
      if (typeof window !== 'undefined') window.__atlasDataset = null
    } finally {
      loading.value = false
    }
  }

  async function ensureMetricsLoaded(opts?: { force?: boolean }) {
    const profile = profiles.value.find((p) => p.id === activeProfileId.value)
    if (profile?.type !== 'nestApi') return
    if (!dataset.value) return
    if (!opts?.force && dataset.value.metrics?.clusters?.length) return
    if (metricsLoading.value) return

    metricsLoading.value = true
    metricsError.value = null
    try {
      const metrics = await loadMetricsFromNestApi(selectedSnapshotId.value ?? undefined)
      if (dataset.value) {
        dataset.value = {
          ...dataset.value,
          metrics,
          fileStatus: {
            ...dataset.value.fileStatus,
            metrics: metrics?.clusters?.length ? 'ok' : 'missing',
          },
          deferred: {
            ...dataset.value.deferred,
            metrics: false,
          },
        }
        if (typeof window !== 'undefined') window.__atlasDataset = dataset.value
      }
    } catch (e) {
      metricsError.value = friendlyAtlasLoadError(e)
    } finally {
      metricsLoading.value = false
    }
  }

  async function refreshData() {
    if (activeProfileId.value) await loadData(undefined, { force: true })
  }

  /**
   * Reload the dataset, optionally scoped to a historical snapshot.
   * When `snapshotId` is provided the caller should ensure the backend
   * dataset endpoint supports `?snapshotId=` filtering.
   */
  async function loadDataset(opts?: { snapshotId?: string }) {
    if (opts?.snapshotId) {
      selectedSnapshotId.value = opts.snapshotId
    } else {
      selectedSnapshotId.value = null
    }
    await loadData(undefined, { force: true })
  }

  async function setTimeRange(range: TimeRange) {
    timeRange.value = range
    localStorage.setItem(TIMERANGE_KEY, range)
    // Sync cost preset so the cost page reflects the same window chosen in the source bar
    const costMap: Partial<Record<TimeRange, CostPreset>> = {
      '3m': 'last3m',
      '6m': 'last6m',
      '12m': 'last12m',
      all: 'all',
      '90d': 'last3m',
      '30d': 'last3m',
      '14d': 'last3m',
      '7d': 'last3m'
    }
    if (costMap[range]) setCostPreset(costMap[range] as CostPreset)
    if (activeProfile.value?.type === 'nestApi') {
      await loadData(undefined, { force: true })
    }
  }

  function setRefreshInterval(ms: number) {
    refreshInterval.value = ms
    localStorage.setItem(REFRESH_KEY, String(ms))
    if (liveRefreshEnabled.value) {
      startLiveRefresh()
    }
  }

  function saveCollectConfig(config: CollectConfig) {
    collectConfig.value = config
    try {
      localStorage.setItem(COLLECT_CONFIG_KEY, JSON.stringify(config))
    } catch {
      /* quota */
    }
  }

  async function fetchProjects() {
    if (projectsLoaded.value) return
    try {
      const resp = await fetch(
        platformDataUrl('/platform-data/query-ai/atlas-intel/collect/projects'),
        {
          headers: authHeaders(),
          signal: AbortSignal.timeout(10000)
        }
      )
      if (!resp.ok) return
      const data = await resp.json()
      availableProjects.value = data.projects ?? []
      credentialsConfigured.value = data.credentialsConfigured ?? false
      projectsLoaded.value = true
      // Pre-select default project from server env
      if (data.defaultProjectId && !collectConfig.value?.projectIds?.length) {
        saveCollectConfig({ projectIds: [data.defaultProjectId] })
      }
    } catch {
      /* atlas CLI may not be available */
    }
  }

  async function fetchFreshness() {
    try {
      const resp = await fetch(platformDataUrl('/platform-data/query-ai/atlas-intel/health'), {
        headers: authHeaders(),
        signal: AbortSignal.timeout(5000)
      })
      if (!resp.ok) return
      const data = await resp.json()
      if (data.lastCollectedAt) lastCollectedAt.value = data.lastCollectedAt
    } catch {
      /* ignore */
    }
  }

  function clearCollectionJob() {
    isCollecting.value = false
    collectionJobId.value = null
    localStorage.removeItem(COLLECTION_JOB_KEY)
  }

  function rememberReportJob(jobId: string) {
    localStorage.setItem(LAST_REPORT_JOB_KEY, jobId)
  }

  async function pollCollectionJob(jobId: string) {
    _stopCollectionPoll = false
    const poll = async () => {
      if (_stopCollectionPoll) return
      try {
        const resp = await fetch(
          platformDataUrl('/platform-data/query-ai/atlas-intel/collect/status'),
          {
            headers: authHeaders()
          }
        )
        if (!resp.ok) {
          // Server not reachable — may have restarted; clear stale job
          clearCollectionJob()
          return
        }
        const status = await resp.json()
        const job = (status.jobs ?? []).find(
          (j: { id: string; status: string; progress?: string[]; error?: string; report?: JobReport }) =>
            j.id === jobId
        )
        if (!job) {
          // The live job may have rolled out of memory, but its durable report
          // can still drive retry/rollup after refresh.
          await fetchJobReportState(jobId)
          clearCollectionJob()
          return
        }
        rememberReportJob(job.id)
        // Keep live logs in state (for admin page log viewer)
        if (job.progress?.length) collectJobLogs.value = job.progress
        // Surface the structured failure report. Backward-compatible: when the
        // BE doesn't include the field (older deployment) we leave the prior
        // value alone so the FE doesn't blow away an in-progress report on
        // partial responses.
        if (job.report) currentJobReport.value = job.report
        if (job.status === 'complete') {
          clearCollectionJob()
          await fetchFreshness()
          await loadData(undefined, { force: true })
          // After collection, switch to last3m so user sees 2 complete months + current MTD
          if (costPreset.value === 'current') setCostPreset('last3m')
        } else if (job.status === 'error') {
          clearCollectionJob()
          loadError.value = job.error ?? 'Collection failed'
        } else if (job.status === 'cancelled') {
          clearCollectionJob()
          await fetchJobReportState(job.id)
        } else {
          if (!_stopCollectionPoll) setTimeout(poll, 5000)
        }
      } catch {
        if (!_stopCollectionPoll) setTimeout(poll, 10000)
      }
    }
    if (!_stopCollectionPoll) setTimeout(poll, 5000)
  }

  async function triggerMetricsCollect() {
    const cfg = collectConfig.value
    // Only send projectIds/projectId if we actually have them — if collectConfig is null,
    // send an empty body so the backend falls back to QUERY_AI_ATLAS_PROJECT_ID env var.
    const body: CollectConfig = cfg
      ? {
          projectIds: cfg.projectIds ?? [],
          projectId: cfg.projectId,
          parallelism: cfg.parallelism,
          projectParallelism: cfg.projectParallelism
        }
      : {}
    isCollecting.value = true
    loadError.value = null
    try {
      const resp = await fetch(
        platformDataUrl('/platform-data/query-ai/atlas-intel/collect/metrics'),
        {
          method: 'POST',
          headers: { ...authHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify(body)
        }
      )
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}))
        throw new Error((err as { message?: string }).message ?? `HTTP ${resp.status}`)
      }
      const result = await resp.json()
      collectionJobId.value = result.jobId
      localStorage.setItem(COLLECTION_JOB_KEY, result.jobId)
      rememberReportJob(result.jobId)
      await pollCollectionJob(result.jobId)
    } catch (e) {
      isCollecting.value = false
      loadError.value = e instanceof Error ? e.message : 'Failed to trigger metrics collection'
    }
  }

  /**
   * Force-release the global collection lock. Use when a previous Recollect
   * crashed (pod restart, OOM, network disconnect) and left a stale lock that
   * blocks new runs. Returns the prior holder for audit/UI confirmation.
   */
  async function forceReleaseCollectionLock(): Promise<{
    released: boolean
    priorHolder?: string
    priorExpiresAt?: string
    message?: string
  }> {
    try {
      const resp = await fetch(
        platformDataUrl('/platform-data/query-ai/atlas-intel/collect/force-release-lock'),
        {
          method: 'POST',
          headers: { ...authHeaders(), 'Content-Type': 'application/json' }
        }
      )
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}))
        const msg = (err as { message?: string }).message ?? `HTTP ${resp.status}`
        return { released: false, message: msg }
      }
      const result = (await resp.json()) as {
        released: boolean
        priorHolder?: string
        priorExpiresAt?: string
        message?: string
      }
      // Clear any stale "lock held" error from a previous failed Recollect
      // so the user can immediately retry without manually dismissing it.
      if (result.released && loadError.value && /collection is already running/i.test(loadError.value)) {
        loadError.value = null
      }
      return result
    } catch (e) {
      return {
        released: false,
        message: e instanceof Error ? e.message : 'Force-release request failed'
      }
    }
  }

  async function stopCollection(jobId?: string): Promise<StopCollectionResult> {
    const targetJobId = jobId ?? collectionJobId.value ?? undefined
    _stopCollectionPoll = true
    try {
      const resp = await fetch(
        platformDataUrl('/platform-data/query-ai/atlas-intel/collect/stop'),
        {
          method: 'POST',
          headers: { ...authHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify({ jobId: targetJobId, forceReleaseLock: true })
        }
      )
      const result = (await resp.json().catch(() => ({}))) as StopCollectionResult
      if (!resp.ok) {
        if (resp.status === 404) {
          const fallback = await stopCollectionLegacyFallback(targetJobId)
          finalizeStoppedCollection(fallback, targetJobId)
          return fallback
        }
        throw new Error(result.message ?? `HTTP ${resp.status}`)
      }
      finalizeStoppedCollection(result, targetJobId)
      return result
    } catch (e) {
      _stopCollectionPoll = false
      const message = e instanceof Error ? e.message : 'Stop request failed'
      loadError.value = message
      return { message }
    }
  }

  async function stopCollectionLegacyFallback(targetJobId?: string): Promise<StopCollectionResult> {
    const stoppedJobs: StopCollectionResult['stoppedJobs'] = []
    let message = 'Stop endpoint is not available on this backend; used legacy cancel fallback'
    if (targetJobId) {
      const cancelResp = await fetch(
        platformDataUrl(
          `/platform-data/query-ai/atlas-intel/collect/cancel/${encodeURIComponent(targetJobId)}`
        ),
        {
          method: 'POST',
          headers: { ...authHeaders(), 'Content-Type': 'application/json' }
        }
      )
      const cancelResult = (await cancelResp.json().catch(() => ({}))) as { message?: string }
      if (cancelResp.ok) {
        stoppedJobs.push({ jobId: targetJobId, status: 'cancelled' })
        message = cancelResult.message ?? message
      } else if (cancelResp.status !== 404) {
        throw new Error(cancelResult.message ?? `Legacy cancel failed: HTTP ${cancelResp.status}`)
      }
    }
    const lockRelease = await forceReleaseCollectionLock()
    return {
      message,
      stoppedJobs,
      killed: { mongosh: 0, atlas: 0 },
      lockRelease
    }
  }

  function finalizeStoppedCollection(result: StopCollectionResult, targetJobId?: string) {
    const stoppedJobId = result.stoppedJobs?.[0]?.jobId ?? targetJobId
    clearCollectionJob()
    loadError.value = null
    if (stoppedJobId) void fetchJobReportState(stoppedJobId)
    else {
      const now = new Date().toISOString()
      currentJobReport.value = currentJobReport.value
        ? { ...currentJobReport.value, status: 'cancelled', completedAt: now }
        : currentJobReport.value
    }
    const killed = result.killed
    const killedSummary = killed ? ` killed mongosh=${killed.mongosh}, atlas=${killed.atlas}` : ''
    collectJobLogs.value = [
      ...collectJobLogs.value,
      `[${new Date().toTimeString().slice(0, 8)}] Stop requested.${killedSummary}`
    ]
  }

  /**
   * Pull the structured failure report for a job. Useful when the recent-jobs
   * window has rolled over (the backend keeps reports in memory longer than
   * the 20-job /collect/status window).
   */
  async function fetchJobReportState(jobId: string): Promise<CollectionReportState | null> {
    try {
      const resp = await fetch(
        platformDataUrl(`/platform-data/query-ai/atlas-intel/collect/report/${jobId}/state`),
        { headers: authHeaders() }
      )
      if (!resp.ok) return null
      const state = (await resp.json()) as CollectionReportState
      currentJobReport.value = state.report
      if (state.progress?.length) collectJobLogs.value = state.progress
      rememberReportJob(jobId)
      return state
    } catch {
      return null
    }
  }

  async function fetchJobReport(jobId: string): Promise<JobReport | null> {
    const state = await fetchJobReportState(jobId)
    if (state?.report) return state.report
    try {
      const resp = await fetch(
        platformDataUrl(`/platform-data/query-ai/atlas-intel/collect/report/${jobId}`),
        { headers: authHeaders() }
      )
      if (!resp.ok) return null
      const r = (await resp.json()) as JobReport
      currentJobReport.value = r
      rememberReportJob(jobId)
      return r
    } catch {
      return null
    }
  }

  async function hydrateLastCollectionReport(): Promise<JobReport | null> {
    const candidates = [collectionJobId.value, localStorage.getItem(LAST_REPORT_JOB_KEY)]
      .filter((id): id is string => !!id)
    for (const jobId of [...new Set(candidates)]) {
      const report = await fetchJobReport(jobId)
      if (report) return report
    }
    return null
  }

  /**
   * Dispatch /collect/retry. The backend launches a new sub-job (id =
   * `retry_<orig>_<n>`) that touches only the failed entities against the
   * SAME snapshot. We swap our active jobId / polling onto the sub-job so
   * the live log + failure report follow the retry.
   *
   * - failureIds omitted ⇒ retry all retryable failures.
   * - rollupAfter ⇒ run insertClusterWorkload after every retried phase.
   */
  async function retryFailures(
    jobId: string,
    failureIds?: string[],
    rollupAfter?: boolean
  ): Promise<{ jobId: string | null; status?: string; message?: string }> {
    try {
      const resp = await fetch(
        platformDataUrl('/platform-data/query-ai/atlas-intel/collect/retry'),
        {
          method: 'POST',
          headers: { ...authHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify({ jobId, failureIds, rollupAfter })
        }
      )
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}))
        throw new Error((err as { message?: string }).message ?? `HTTP ${resp.status}`)
      }
      const result = (await resp.json()) as {
        jobId: string | null
        status?: string
        message?: string
      }
      if (result.jobId) {
        // Re-attach polling onto the retry sub-job. The retry sub-job has its
        // own report (only NEW failures from this attempt) so users can
        // iterate retry → smaller report → retry → … until clean.
        collectionJobId.value = result.jobId
        localStorage.setItem(COLLECTION_JOB_KEY, result.jobId)
        rememberReportJob(result.jobId)
        isCollecting.value = true
        currentJobReport.value = null
        await pollCollectionJob(result.jobId)
      }
      return result
    } catch (e) {
      return { jobId: null, message: e instanceof Error ? e.message : String(e) }
    }
  }

  /**
   * Manually re-run the cluster workload rollup against the snapshot bound
   * to a job. Returns counts so the UI can confirm "N projects rolled up".
   */
  async function rollupSnapshot(
    jobId: string
  ): Promise<{ snapshotId?: string; clustersRolledUp: number; errors: { projectId: string; error: string }[] }> {
    try {
      const resp = await fetch(
        platformDataUrl('/platform-data/query-ai/atlas-intel/collect/rollup'),
        {
          method: 'POST',
          headers: { ...authHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify({ jobId })
        }
      )
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}))
        throw new Error((err as { message?: string }).message ?? `HTTP ${resp.status}`)
      }
      return (await resp.json()) as {
        snapshotId?: string
        clustersRolledUp: number
        errors: { projectId: string; error: string }[]
      }
    } catch (e) {
      return { clustersRolledUp: 0, errors: [{ projectId: '', error: e instanceof Error ? e.message : String(e) }] }
    }
  }

  async function triggerRecollect(config?: CollectConfig) {
    const cfg = config ?? collectConfig.value
    // Only send project selection + collection params — credentials are server-side
    const body: CollectConfig = {
      projectIds: cfg?.projectIds ?? [],
      projectId: cfg?.projectId,
      parallelism: cfg?.parallelism,
      projectParallelism: cfg?.projectParallelism,
      monthsBack: cfg?.monthsBack
    }
    isCollecting.value = true
    loadError.value = null
    try {
      const resp = await fetch(
        platformDataUrl('/platform-data/query-ai/atlas-intel/collect/full'),
        {
          method: 'POST',
          headers: { ...authHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify(body)
        }
      )
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}))
        throw new Error((err as { message?: string }).message ?? `HTTP ${resp.status}`)
      }
      const result = await resp.json()
      collectionJobId.value = result.jobId
      localStorage.setItem(COLLECTION_JOB_KEY, result.jobId)
      rememberReportJob(result.jobId)
      if (cfg) saveCollectConfig(cfg)
      await pollCollectionJob(result.jobId)
    } catch (e) {
      isCollecting.value = false
      loadError.value = e instanceof Error ? e.message : 'Failed to trigger collection'
    }
  }

  function startLiveRefresh(intervalMs?: number) {
    stopLiveRefresh()
    if (activeProfile.value?.type !== 'nestApi') return
    liveRefreshEnabled.value = true
    const ms = intervalMs ?? refreshInterval.value
    liveRefreshInterval.value = setInterval(() => loadData(undefined, { force: true }), ms)
  }

  function stopLiveRefresh() {
    liveRefreshEnabled.value = false
    if (liveRefreshInterval.value) {
      clearInterval(liveRefreshInterval.value)
      liveRefreshInterval.value = null
    }
  }

  async function handleFileUpload(files: FileList | File[]) {
    const loadedFiles: Record<string, string> = {}
    const missingKeys: AtlasFileKey[] = []

    for (const file of Array.from(files)) {
      const key = FILE_KEY_BY_NAME[file.name]
      if (key) {
        const text = await readFileAsText(file)
        loadedFiles[key] = text // stored as-is; fetchText detects non-URL strings
      }
    }

    // Check required files
    for (const [k] of Object.entries(ATLAS_FILE_NAMES) as [AtlasFileKey, string][]) {
      if (!loadedFiles[k]) missingKeys.push(k)
    }

    const existingFileProfile = profiles.value.find((p) => p.type === 'localFiles')
    if (existingFileProfile) {
      const merged = { ...(existingFileProfile.loadedFiles ?? {}), ...loadedFiles } as Record<
        AtlasFileKey,
        string
      >
      updateProfile(existingFileProfile.id, { loadedFiles: merged })
      await loadData(existingFileProfile.id)
    } else {
      const profile = addProfile({
        label: 'Local Files',
        type: 'localFiles',
        loadedFiles: loadedFiles as Record<AtlasFileKey, string>
      })
      await loadData(profile.id)
    }
  }

  function setOpenaiKey(key: string) {
    openaiKey.value = key
    if (key) localStorage.setItem(OPENAI_KEY, key)
    else localStorage.removeItem(OPENAI_KEY)
  }

  // ── Diagnostics: Connectivity Probe (async job — live logs via status polling) ─
  const isProbing = ref(false)
  const probeError = ref<string | null>(null)
  const probeResults = ref<ConnectivityProbeReport | null>(null)

  // Restore probe state from localStorage so it survives page refresh
  const _storedProbeJobId = localStorage.getItem('atlas_probe_job_id')
  const _storedProbeStatus = localStorage.getItem('atlas_probe_status')
  const probeJobId = ref<string | null>(_storedProbeJobId)
  const probeLogs = ref<string[]>([])
  const probeStatus = ref<'idle' | 'running' | 'complete' | 'error'>(
    (_storedProbeStatus as 'idle' | 'running' | 'complete' | 'error') ?? 'idle'
  )

  let _probePollTimer: ReturnType<typeof setInterval> | null = null
  let _collectPollTimer: ReturnType<typeof setInterval> | null = null
  let _stopCollectionPoll = false

  function _stopProbePoll() {
    if (_probePollTimer) {
      clearInterval(_probePollTimer)
      _probePollTimer = null
    }
  }

  function cleanupTimers() {
    _stopCollectionPoll = true
    _stopProbePoll()
    if (_collectPollTimer) {
      clearInterval(_collectPollTimer)
      _collectPollTimer = null
    }
  }

  // Persist probeJobId / probeStatus across page refreshes
  watch(probeJobId, (v) => {
    if (v) localStorage.setItem('atlas_probe_job_id', v)
    else localStorage.removeItem('atlas_probe_job_id')
  })
  watch(probeStatus, (v) => {
    localStorage.setItem('atlas_probe_status', v)
  })

  async function _pollProbeJob(jobId: string) {
    _stopProbePoll()
    _probePollTimer = setInterval(async () => {
      try {
        const resp = await fetch(
          platformDataUrl('/platform-data/query-ai/atlas-intel/collect/status'),
          { headers: authHeaders() }
        )
        if (!resp.ok) return
        const data = (await resp.json()) as {
          jobs?: Array<{ id: string; status: string; progress?: string[] }>
        }
        const job = data.jobs?.find((j) => j.id === jobId)
        if (!job) return
        probeLogs.value = job.progress ?? []
        if (job.status === 'complete' || job.status === 'error') {
          probeStatus.value = job.status as 'complete' | 'error'
          isProbing.value = false
          _stopProbePoll()
        }
      } catch {
        /* ignore transient poll errors */
      }
    }, 1500)
  }

  async function startProbeJob(projectIds?: string[]) {
    if (activeProfile.value?.type !== 'nestApi') {
      probeError.value = 'Probe requires a NestAPI connection profile'
      return
    }
    const ids = projectIds?.length ? projectIds : (collectConfig.value?.projectIds ?? [])
    isProbing.value = true
    probeError.value = null
    probeLogs.value = []
    probeStatus.value = 'running'
    probeJobId.value = null
    _stopProbePoll()
    try {
      const resp = await fetch(platformDataUrl('/platform-data/query-ai/atlas-intel/admin/probe'), {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ projectIds: ids })
      })
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}))
        throw new Error((err as { message?: string }).message ?? `HTTP ${resp.status}`)
      }
      const result = (await resp.json()) as { jobId: string }
      probeJobId.value = result.jobId
      await _pollProbeJob(result.jobId)
    } catch (e) {
      probeError.value = e instanceof Error ? e.message : 'Probe failed'
      isProbing.value = false
      probeStatus.value = 'error'
    }
  }

  async function probeConnectivity(projectIds?: string[]) {
    await startProbeJob(projectIds)
  }

  function clearProbe() {
    _stopProbePoll()
    probeResults.value = null
    probeError.value = null
    probeLogs.value = []
    probeStatus.value = 'idle'
    probeJobId.value = null
    isProbing.value = false
  }

  function createQuickProfile(type: SourceType, baseUrl: string, label?: string): SourceProfile {
    const profile = addProfile({
      type,
      baseUrl: baseUrl.trim(),
      label: label || (type === 'gcpBaseUrl' ? 'GCP Bucket' : 'Local Server')
    })
    return profile
  }

  async function autoDetectApi(): Promise<boolean> {
    try {
      const resp = await fetch(platformDataUrl('/platform-data/query-ai/atlas-intel/health'), {
        headers: authHeaders(),
        signal: AbortSignal.timeout(3000)
      })
      if (!resp.ok) return false
      const data = await resp.json()
      // Note: project defaults are loaded lazily via fetchProjects() when the collect panel opens
      return data.status === 'ok' || data.status === 'no_data'
    } catch {
      return false
    }
  }

  // On store init: probe NestJS API, ensure a nestApi profile exists, and load data
  if (typeof window !== 'undefined') {
    autoDetectApi().then((reachable) => {
      if (!reachable) return
      const existingNestApi = profiles.value.find((p) => p.type === 'nestApi')
      const nestProfile = existingNestApi ?? addProfile({ type: 'nestApi', label: 'Platform API' })

      // Always pre-fetch project names so filter dropdown shows names not IDs
      fetchProjects()

      if (!activeProfileId.value) {
        // No active profile → default to Platform API
        loadData(nestProfile.id)
      } else if (!dataset.value) {
        // Profile is saved but dataset is empty (page refresh) → reload
        // If the active profile is the nestApi one or there's no other data, prefer nestApi
        const activeIsNest = activeProfileId.value === nestProfile.id
        loadData(activeIsNest ? nestProfile.id : activeProfileId.value)
      }
    })
  }

  // ─── Check if profile was set via URL param ───────────────────────────────
  function initFromQueryParam() {
    if (typeof window === 'undefined') return
    const params = new URLSearchParams(window.location.search)
    const encoded = params.get('atlasSource')
    if (!encoded) return
    try {
      const profile = JSON.parse(atob(encoded)) as Partial<SourceProfile>
      if (profile.type && profile.baseUrl) {
        const created = createQuickProfile(profile.type, profile.baseUrl, profile.label)
        loadData(created.id)
      }
    } catch {
      // invalid param, ignore
    }
  }

  // ── Auto-resume: if a collection job was running before refresh, re-attach ──
  if (collectionJobId.value) {
    // Verify the job is still running; clear stale state if not
    pollCollectionJob(collectionJobId.value)
  }

  // ── Auto-resume: if a probe job was running before refresh, re-attach ──
  if (_storedProbeJobId && _storedProbeStatus === 'running') {
    isProbing.value = true
    setTimeout(() => _pollProbeJob(_storedProbeJobId), 500)
  }

  return {
    // State
    profiles,
    activeProfileId,
    dataset,
    loading,
    loadError,
    metricsLoading,
    metricsError,
    liveRefreshEnabled,
    searchQuery,
    openaiKey,
    timeRange,
    refreshInterval,
    lastCollectedAt,
    collectionJobId,
    collectJobLogs,
    isCollecting,
    collectConfig,
    // Getters
    activeProfile,
    hasData,
    fileStatuses,
    months,
    numMonths,
    costByCluster,
    totalCost,
    avgMonthlyCost,
    latestMonth,
    latestMonthCost,
    monthToDateCost,
    prevMonthCost,
    momChange,
    tierMap,
    clustersSortedByCost,
    enrichedStorage,
    isCurrentMonthPartial,
    completeMonths,
    latestCompleteMonth,
    orgCosts,
    currentMonthKey,
    // Filtered data
    filterCluster,
    filterProjectId,
    filterProjectIds,
    filterClusters,
    filterServices,
    availableDataProjects,
    availableClusters,
    availableServices,
    availableCostClusters,
    filteredCostsByService,
    selectedSnapshotId,
    snapshotList,
    loadSnapshotList,
    costPreset,
    costCustomStart,
    costCustomEnd,
    costMonthBounds,
    costFilterClusters,
    dateRangeByPage,
    setDateRangeForPage,
    clearDateRangeForPage,
    filteredCosts,
    filteredStorage,
    filteredOps,
    filteredIndexes,
    // Actions
    addProfile,
    updateProfile,
    deleteProfile,
    setActiveProfile,
    loadData,
    loadDataset,
    ensureMetricsLoaded,
    refreshData,
    setTimeRange,
    setRefreshInterval,
    setFilterCluster,
    setFilterProjectId,
    setFilterProjectIds,
    setFilterClusters,
    setFilterServices,
    toggleFilterProject,
    toggleFilterCluster,
    toggleFilterService,
    setSelectedSnapshot,
    setCostPreset,
    setCostCustomRange,
    toggleCostFilterCluster,
    setCostFilterClusters,
    saveCollectConfig,
    fetchProjects,
    fetchFreshness,
    triggerRecollect,
    triggerMetricsCollect,
    forceReleaseCollectionLock,
    stopCollection,
    currentJobReport,
    fetchJobReport,
    hydrateLastCollectionReport,
    retryFailures,
    rollupSnapshot,
    startLiveRefresh,
    stopLiveRefresh,
    handleFileUpload,
    createQuickProfile,
    autoDetectApi,
    initFromQueryParam,
    setOpenaiKey,
    availableProjects,
    credentialsConfigured,
    projectsLoaded,
    // Diagnostics
    probeResults,
    isProbing,
    probeError,
    probeJobId,
    probeLogs,
    probeStatus,
    startProbeJob,
    probeConnectivity,
    clearProbe,
    cleanupTimers
  }
})
